/**
 * A screenplay as a PDF, in the industry's measures: Courier 12, 1.5 inch left margin, 1 inch
 * elsewhere, 6 lines an inch, page numbers top right from page 2, a title page first. The PDF
 * library is loaded the first time one is made, not with the app.
 */
import { PAPER, type Page, type Paper, type TitlePage } from './screenplay';

const PT_PER_CHAR = 7.2; // 10 characters an inch
const LINE_PT = 12; // 6 lines an inch
const LEFT_PT = 108; // 1.5 inches
const TOP_PT = 72; // 1 inch

export const screenplayPdf = async (pages: Page[], titlePage: TitlePage, paper: Paper, fileName: string): Promise<void> => {
    const { jsPDF } = await import('jspdf');
    const measures = PAPER[paper];
    const doc = new jsPDF({ unit: 'pt', format: paper === 'A4' ? 'a4' : 'letter' });
    doc.setFont('courier', 'normal');
    doc.setFontSize(12);
    const centre = measures.widthPt / 2;

    // The title page: the title a third of the way down, who wrote it under it, the date and the contact at the foot
    const title = titlePage.title?.trim();
    if (title) {
        let y = measures.heightPt / 3;
        doc.setFont('courier', 'bold');
        for (const line of title.toUpperCase().split('\n')) { doc.text(line, centre, y, { align: 'center' }); y += LINE_PT * 2; }
        doc.setFont('courier', 'normal');
        y += LINE_PT * 2;
        if (titlePage.author) {
            doc.text(titlePage.credit || 'Written by', centre, y, { align: 'center' }); y += LINE_PT * 2;
            for (const line of titlePage.author.split('\n')) { doc.text(line, centre, y, { align: 'center' }); y += LINE_PT; }
        }
        if (titlePage.source) { y += LINE_PT * 2; for (const line of titlePage.source.split('\n')) { doc.text(line, centre, y, { align: 'center' }); y += LINE_PT; } }
        let foot = measures.heightPt - TOP_PT - LINE_PT * 3;
        const footer = [titlePage['draft date'] || titlePage.date, titlePage.contact, titlePage.copyright].filter(Boolean) as string[];
        for (const block of footer.reverse()) {
            const lines = block.split('\n');
            foot -= LINE_PT * (lines.length - 1);
            lines.forEach((line, i) => doc.text(line, LEFT_PT, foot + i * LINE_PT));
            foot -= LINE_PT * 2;
        }
        doc.addPage();
    }

    pages.forEach((page, index) => {
        if (index > 0) doc.addPage();
        if (page.number > 1) doc.text(`${page.number}.`, measures.widthPt - TOP_PT, TOP_PT / 2 + 6, { align: 'right' });
        let y = TOP_PT + LINE_PT;
        for (const line of page.lines) {
            if (line.kind !== 'blank' && line.text) {
                doc.setFont('courier', line.kind === 'scene_heading' ? 'bold' : 'normal');
                doc.text(line.text, LEFT_PT + line.indent * PT_PER_CHAR, y);
            }
            y += LINE_PT;
        }
    });
    doc.setFont('courier', 'normal');
    doc.save(fileName);
};

/** A safe file name from a title */
export const fileNameFor = (title: string, extension: string): string => `${(title.trim() || 'Screenplay').replace(/[<>:"/\\|?*\x00-\x1f]/g, '_').replace(/\s+/g, ' ').slice(0, 80)}.${extension}`;

/** Hands a file to the browser to save */
export const saveBlob = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
